import { useContext, useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { Space, Tag, Row, Col, Dropdown, Skeleton } from "antd";
import { Pencil, Trash2, Tag as TagIcon } from "lucide-react";

import { AuthContext } from "../../context/AuthContext";
import EditDeleteTag from "./EditDeleteTag";
import { getTagList } from "../../actionCreator/dashboardActionCreator";
import { getClientNotesTags } from "../../actionCreator/leadDetailsActionCreator";
import { ReactComponent as DeleteIcon } from "../../assets/images/webToLead/delete.svg";
import tagIcon from "../../assets/svg/icons/tag.svg";
import { useAlert } from "../../context/AlertProvider";

const TagsReport = ({ allTagSearch = "", userId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, isExecutiveView, isCompanyView, isLeadView } =
    useContext(AuthContext);
  const { showAlert } = useAlert();

  const isMyDashboard = user?.id == userId;

  const [open, setOpen] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const requestRef = useRef(0);

  const executiveCompanyTagList = useSelector(
    (state) => state.DashboardReducer.executiveTags,
  );
  const companyTagList = useSelector(
    (state) => state.DashboardReducer.companyTags,
  );
  const leadTagList = useSelector(
    (state) => state.leadListingReducer?.companyTagList,
  );
  const notesTags = useSelector((state) => state.LeadDetailsReducer?.notesTags);

  useEffect(() => {
    const requestId = ++requestRef.current;
    setIsLoading(true);
    Promise.all([
      dispatch(getTagList("COMPANY", userId)),
      dispatch(getTagList("EXECUTIVE", userId)),
      dispatch(getTagList("LEAD", userId)),
      dispatch(getClientNotesTags(userId)),
    ]).finally(() => {
      if (requestId === requestRef.current) {
        setIsLoading(false);
      }
    });
  }, [dispatch, userId]);

  const doSearchByTag = (item, type) => {
    if (!isMyDashboard) {
      showAlert(
        "info",
        "Redirection is not allowed when viewing another user's dashboard.",
      );
      return;
    }
    if (type === 1) navigate(`/search-company?Tags=${item}`);
    else if (type === 2) navigate(`/search-executive?Tags=${item}`);
    else if (type === 3) navigate(`/search-lead?Tags=${item}`);
    else navigate(`/search-lead?Notes Tags=${item}`);
  };

  const applyCommonSearch = (list) => {
    const value = allTagSearch.trim().toLowerCase();
    if (!value) return list || [];
    return (list || []).filter((item) =>
      item?.name?.toLowerCase().includes(value),
    );
  };

  const renderTagSection = ({ title, data, searchType, emptyText }) => (
    <Col xs={24} lg={12}>
      <div className="tw-p-6 tw-border tw-border-solid tw-border-border-secondary tw-rounded-[10px] tw-bg-white tw-h-full">
        <div className="tw-flex tw-items-center tw-gap-3 tw-text-base tw-font-semibold tw-text-text-primary tw-pb-4 tw-mb-4 tw-border-b tw-border-t-0 tw-border-l-0 tw-border-r-0 tw-border-solid tw-border-border-secondary">
          {title}
          <div className="tw-flex tw-justify-center tw-items-center tw-bg-[#E5E5E5] tw-rounded-full tw-text-sm tw-font-semibold !tw-w-[30px] !tw-h-[30px]">
            {data?.length}
          </div>
        </div>

        <div>
          <div className="tw-mt-1">
            {isLoading ? (
              <Skeleton active paragraph={{ rows: 4 }} title={false} />
            ) : data?.length ? (
              <Space size={[0, data?.length]} wrap>
                {data?.slice(0, 4).map((item, index) => (
                  <Tag
                    key={`${title}-${index}`}
                    className="tw-bg-white tw-py-1 tw-px-4 tw-rounded-[10px] tw-border tw-border-solid tw-border-border-secondary"
                  >
                    <span
                      className="tw-cursor-pointer tw-text-base tw-w-fit tw-inline-flex tw-items-center"
                      onClick={() => doSearchByTag(item?.name, searchType)}
                    >
                      <TagIcon className="tw-w-3.5 tw-h-3.5 tw-mr-2 tw-text-slate-500" />
                      {item?.name}
                    </span>

                    {isMyDashboard && (
                      <>
                        <Pencil
                          className="tw-w-3.5 tw-h-3.5 tw-ml-2 tw-cursor-pointer hover:tw-text-[#5D44FF]"
                          onClick={() => setOpen({ type: "edit", data: item })}
                        />
                        <Trash2
                          className="tw-w-3.5 tw-h-3.5 tw-ml-2 tw-cursor-pointer hover:tw-text-rose-500"
                          onClick={() =>
                            setOpen({ type: "delete", data: item })
                          }
                        />
                      </>
                    )}
                  </Tag>
                ))}

                {data?.length > 4 && (
                  <Dropdown
                    trigger={["click", "hover"]}
                    placement="bottomLeft"
                    overlay={
                      <div className="tw-w-[260px] tw-bg-white tw-rounded-lg tw-shadow-lg tw-p-3">
                        <div className="tw-font-semibold tw-text-sm tw-mb-2 tw-text-center">
                          All Tags ({data?.slice(4).length})
                        </div>
                        <div className="tw-max-h-[300px] tw-overflow-y-auto">
                          {data?.slice(4).map((item, index) => (
                            <div
                              key={index}
                              className="tw-flex tw-justify-between tw-items-center tw-py-2"
                            >
                              <span
                                className="tw-text-sm tw-cursor-pointer tw-inline-flex tw-items-center"
                                onClick={() =>
                                  doSearchByTag(item?.name, searchType)
                                }
                              >
                                <TagIcon className="tw-w-3.5 tw-h-3.5 tw-mr-2 tw-text-slate-500" />
                                <span className="tw-text-text-primary">
                                  {item?.name}
                                </span>
                              </span>
                              {isMyDashboard && (
                                <span>
                                  <Pencil
                                    className="tw-w-3.5 tw-h-3.5 tw-ml-2 tw-cursor-pointer hover:tw-text-[#5D44FF]"
                                    onClick={() =>
                                      setOpen({ type: "edit", data: item })
                                    }
                                  />
                                  <Trash2
                                    className="tw-w-3.5 tw-h-3.5 tw-mx-2 tw-cursor-pointer hover:tw-text-rose-500"
                                    onClick={() =>
                                      setOpen({ type: "delete", data: item })
                                    }
                                  />
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    }
                  >
                    <Tag className="tw-bg-white tw-py-1 tw-px-4 tw-rounded-[10px] tw-border tw-border-solid tw-border-border-secondary">
                      <span className="tw-cursor-pointer tw-text-base tw-w-fit">
                        <img src={tagIcon} alt="" className="tw-mr-2" /> +{" "}
                        {data?.slice(4).length}
                      </span>
                    </Tag>
                  </Dropdown>
                )}
              </Space>
            ) : (
              <NoTags text={emptyText} />
            )}
          </div>
        </div>
      </div>
    </Col>
  );

  return (
    <>
      <Row gutter={[20, 20]} className="tw-mt-6">
        {isCompanyView &&
          renderTagSection({
            title: "Company Tags",
            data: applyCommonSearch(companyTagList),
            searchType: 1,
            emptyText: "companies",
          })}

        {isExecutiveView &&
          renderTagSection({
            title: "Executive Tags",
            data: applyCommonSearch(executiveCompanyTagList),
            searchType: 2,
            emptyText: "executives",
          })}

        {isLeadView &&
          renderTagSection({
            title: "Lead Tags",
            data: applyCommonSearch(leadTagList),
            searchType: 3,
            emptyText: "leads",
          })}

        {isLeadView &&
          renderTagSection({
            title: "Notes Tags",
            data: applyCommonSearch(notesTags),
            searchType: 4,
            emptyText: "notes",
          })}

        <EditDeleteTag
          isOpen={open}
          handleCancel={() => setOpen()}
          data={open}
          userId={userId}
        />
      </Row>
    </>
  );
};

export default TagsReport;

const NoTags = ({ text }) => {
  return (
    <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-1 tw-my-4">
      <div className="tw-flex tw-justify-center tw-items-center tw-bg-[#E5E5E5] tw-rounded-full tw-text-sm tw-font-semibold !tw-w-[36px] !tw-h-[36px]">
        <img src={tagIcon} alt="" />
      </div>
      <div className="tw-mt-1 tw-text-base tw-font-semibold tw-text-text-primary">
        No tags created yet.
      </div>
      <div className="tw-text-xs tw-font-semibold tw-text-sub-text">
        Organize your {text} with custom tags.
      </div>
    </div>
  );
};
